import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, X, Loader2 } from 'lucide-react';
import { ChapterType, SectionType } from '../lib/database.types';

const bonusSectionSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  type: z.enum(['glossary', 'checklist', 'appendix', 'bonus', 'faq'] as const),
  sectionType: z.enum(['glossary', 'checklist', 'resources', 'exercises', 'summary', 'faq'] as const),
  notes: z.string().optional(),
});

type BonusSectionFormData = z.infer<typeof bonusSectionSchema>;

interface AddBonusSectionProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: {
    chapter_title: string;
    chapter_type: ChapterType;
    section_type: SectionType;
    notes?: string;
  }) => Promise<void>;
  saving: boolean;
}

export function AddBonusSection({ isOpen, onClose, onAdd, saving }: AddBonusSectionProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BonusSectionFormData>({
    resolver: zodResolver(bonusSectionSchema),
    defaultValues: {
      type: 'glossary',
      sectionType: 'glossary',
    },
  });

  if (!isOpen) return null;

  const onSubmit = async (data: BonusSectionFormData) => {
    await onAdd({
      chapter_title: data.title,
      chapter_type: data.type,
      section_type: data.sectionType,
      notes: data.notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Add Bonus Section
              </h3>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Title
                    </label>
                    <input
                      type="text"
                      {...register('title')}
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="e.g., Glossary of Key Terms"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Use a title specific to your book topic, not generic.
                    </p>
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Section Type
                    </label>
                    <select
                      {...register('type')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="glossary">Glossary</option>
                      <option value="checklist">Checklist</option>
                      <option value="appendix">Appendix</option>
                      <option value="faq">FAQ</option>
                      <option value="bonus">Other Bonus Content</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="sectionType" className="block text-sm font-medium text-gray-700">
                      Content Format
                    </label>
                    <select
                      {...register('sectionType')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="glossary">Glossary Entries</option>
                      <option value="checklist">Checklist Items</option>
                      <option value="resources">Resource Links</option>
                      <option value="exercises">Practice Exercises</option>
                      <option value="summary">Summary Points</option>
                      <option value="faq">FAQ Format</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <textarea
                      {...register('notes')}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="Add any specific instructions or content for this section..."
                    />
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Add Section
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}